// filepath: apps/web/src/renderers/task/TaskClaim.tsx
//
// Claim button for unclaimed tasks (FR-HT-2 / FR-UI-TASK-1).
//
// Renders a "Claim" button that:
//   - Calls useClaimTask mutation on click.
//   - Shows a loading spinner while the mutation is pending.
//   - Shows a 409 error state gracefully (task was claimed by someone else).
//   - On success, calls onClaimed so the parent can navigate to TaskComplete.
//
// AC-3: Claiming optimistically removes from the unclaimed list.

import React from "react";
import { useClaimTask } from "@/features/tasks/useClaimTask";
import { MIN_TOUCH_TARGET_PX } from "@/runtime/useIsMobile";

export interface TaskClaimProps {
  taskId: string;
  /** Called on successful claim — parent navigates to the completion form. */
  onClaimed?: (taskId: string) => void;
  /** When true, renders a full-width button suitable for mobile card layout. */
  isMobile?: boolean;
}

/**
 * TaskClaim — renders a "Claim" button for unclaimed tasks.
 *
 * The button is only shown for tasks with status 'unclaimed'.
 * The parent is responsible for filtering which tasks to show this button for.
 *
 * FR-UI-NFR-3 AC-3: on mobile, button is full-width with 48×48dp touch target.
 */
export function TaskClaim({
  taskId,
  onClaimed,
  isMobile = false,
}: TaskClaimProps): React.ReactElement {
  const { mutate, isPending, isError, error } = useClaimTask();

  function handleClaim(): void {
    mutate(
      { taskId },
      {
        onSuccess: () => {
          onClaimed?.(taskId);
        },
      },
    );
  }

  if (isError && error && "status" in error && error.status === 409) {
    // Task was claimed by another user between render and click.
    // Show a brief "Already claimed" message — the unclaimed list
    // will refetch and remove this task.
    return (
      <span
        data-testid="task-claim-conflict"
        style={{ color: "#92400e", fontSize: "12px" }}
      >
        Already claimed
      </span>
    );
  }

  return (
    <button
      data-testid="task-claim-button"
      type="button"
      disabled={isPending}
      onClick={handleClaim}
      style={{
        width: isMobile ? "100%" : "auto",
        padding: isMobile ? "12px 16px" : "4px 12px",
        minHeight: `${MIN_TOUCH_TARGET_PX}px`,
        borderRadius: "var(--radius-md)",
        border: "1px solid #3b82f6",
        backgroundColor: isPending ? "#93c5fd" : "#3b82f6",
        color: "#ffffff",
        fontSize: isMobile ? "16px" : "13px",
        fontWeight: 500,
        cursor: isPending ? "not-allowed" : "pointer",
        transition: "background-color 0.15s ease",
        boxSizing: "border-box",
      }}
    >
      {isPending ? "Claiming..." : "Claim"}
    </button>
  );
}
